// exportEvidence: may download raw evidence files (photo/video) via the
// exportEvidence Cloud Function, which also writes the evidence_exports audit
// record. Supervisor and above. CLIENT/LANDLORD IS EXCLUDED ON PURPOSE: the
// client is the customer, not the evidence custodian — they can read incident
// reports, but raw evidence files are released deliberately by our team, not
// self-service (chain of custody + data protection: files may contain third
// parties). Enforced server-side in the function and in Firestore rules
// (canExportEvidence) — the UI flag below only controls button visibility.
//
// SCHEMA-DRIFT BUG (cleanup backlog): `facilities_manager` (level 4) and
// `facility_manager` (level 6, "legacy") are two names for what is effectively
// one role. Both are granted below so nothing breaks, but this WILL cause a
// permissions bug eventually (an account saved under one spelling, gated on the
// other). Needs consolidating to a single canonical role id everywhere
// (roles.js, firestore.rules isManager()/canExportEvidence(), any assignment UI).
export const ROLES = {
  client: {
    label: 'Client / Landlord',
    color: '#C9A84C',
    level: 6,
    permissions: {
      viewLiveOps: true,
      viewEmergency: true,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: true,
      viewTeam: true,
      viewFacilities: true,
      viewBilling: true,
      createAlerts: true,
      approveReports: true,
      manageTeam: true,
      manageSites: true,
      updateThreatLevel: true,
      exportData: true,
      exportEvidence: false,
      manageCheckpoints: false,
    }
  },
  general_manager: {
    label: 'General Manager',
    color: '#7C3AED',
    level: 5,
    permissions: {
      viewLiveOps: true,
      viewEmergency: true,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: true,
      viewTeam: true,
      viewFacilities: true,
      viewBilling: false,
      createAlerts: true,
      approveReports: true,
      manageTeam: true,
      manageSites: true,
      updateThreatLevel: true,
      exportData: true,
      exportEvidence: true,
      manageCheckpoints: true,
    }
  },
  facilities_manager: {
    label: 'Facilities Manager',
    color: '#1565C0',
    level: 4,
    permissions: {
      viewLiveOps: true,
      viewEmergency: true,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: true,
      viewTeam: true,
      viewFacilities: true,
      viewBilling: false,
      createAlerts: false,
      approveReports: true,
      manageTeam: false,
      manageSites: false,
      updateThreatLevel: false,
      exportData: true,
      exportEvidence: true,
      manageCheckpoints: true,
    }
  },
  head_of_security: {
    label: 'Head of Security',
    color: '#1E3A5F',
    level: 3,
    permissions: {
      viewLiveOps: true,
      viewEmergency: true,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: true,
      viewTeam: true,
      viewFacilities: false,
      viewBilling: false,
      createAlerts: true,
      approveReports: true,
      manageTeam: true,
      manageSites: false,
      updateThreatLevel: true,
      exportData: true,
      exportEvidence: true,
      manageCheckpoints: true,
    }
  },
  supervisor: {
    label: 'Supervisor',
    color: '#2E7D32',
    level: 2,
    permissions: {
      viewLiveOps: true,
      viewEmergency: false,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: false,
      viewTeam: false,
      viewFacilities: false,
      viewBilling: false,
      createAlerts: false,
      approveReports: true,
      manageTeam: false,
      manageSites: false,
      updateThreatLevel: false,
      exportData: false,
      exportEvidence: true,
      manageCheckpoints: true,
    }
  },
  officer: {
    label: 'Security Officer',
    color: '#6B7280',
    level: 1,
    permissions: {
      viewLiveOps: false,
      viewEmergency: false,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: false,
      viewTeam: false,
      viewFacilities: false,
      viewBilling: false,
      createAlerts: false,
      approveReports: false,
      manageTeam: false,
      manageSites: false,
      updateThreatLevel: false,
      exportData: false,
      exportEvidence: false,
      manageCheckpoints: false,
    }
  },
  // Legacy support
  admin: {
    label: 'Admin',
    color: '#C9A84C',
    level: 6,
    permissions: {
      viewLiveOps: true,
      viewEmergency: true,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: true,
      viewTeam: true,
      viewFacilities: true,
      viewBilling: true,
      createAlerts: true,
      approveReports: true,
      manageTeam: true,
      manageSites: true,
      updateThreatLevel: true,
      exportData: true,
      exportEvidence: true,
      manageCheckpoints: true,
    }
  },
  facility_manager: {
    label: 'Facility Manager',
    color: '#C9A84C',
    level: 6,
    permissions: {
      viewLiveOps: true,
      viewEmergency: true,
      viewIncidents: true,
      viewPatrols: true,
      viewAnalytics: true,
      viewTeam: true,
      viewFacilities: true,
      viewBilling: true,
      createAlerts: true,
      approveReports: true,
      manageTeam: true,
      manageSites: true,
      updateThreatLevel: true,
      exportData: true,
      exportEvidence: true,
      manageCheckpoints: true,
    }
  },
};

export const getRole = (roleStr) => ROLES[roleStr] || ROLES.officer;
export const can = (roleStr, permission) => getRole(roleStr).permissions[permission] || false;
export const getRoleColor = (roleStr) => getRole(roleStr).color;
export const getRoleLabel = (roleStr) => getRole(roleStr).label;
export const getRoleLevel = (roleStr) => getRole(roleStr).level;
